




import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import companyApi from '../api/companyApi';

const CompanyForm = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    yearOfEstablishment: '',
    organizationType: '',
    hsCode: '',
    industrySectors: '',
    productsServices: '',
    productCatalogue: '',
    productCatalogueType: '',
    salesTurnover: '',
    contactName: '',
    designation: '',
    email: '',
    phone: '',
    website: '',
    registeredOffice: '',
    corporateOffice: '',
    state: '',
    city: '',
    latitude: '',
    longitude: '',
    password: '',
    password2: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocationSelect = ({ latitude, longitude }) => {
    setFormData({ ...formData, latitude, longitude });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password2) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ text: '', type: '' });

    try {
      // Create FormData for file upload and registration
      const formDataToSend = new FormData();

      // Map form fields to backend expected field names
      const fieldMapping = {
        companyName: formData.companyName,
        directorCeo: formData.contactName, // Map contactName to directorCeo
        industry: formData.industrySectors, // Map industrySectors to industry
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        password: formData.password,
        password2: formData.password2
      };

      // Add only the required fields to FormData
      Object.entries(fieldMapping).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formDataToSend.append(key, value);
        }
      });

      // Register the company and user
      // Don't set Content-Type manually — axios sets it automatically for
      // FormData with the correct multipart boundary.
      const response = await companyApi.registerCompany(formDataToSend, {});

      // Only proceed if we have a successful response with token
      if (response && response.token) {
        setMessage({ text: 'Company registered successfully! You are now logged in.', type: 'success' });
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        throw new Error('Registration failed: No token received');
      }
    } catch (error) {
      console.error('Registration error:', error);

      // Handle different types of errors with more details
      if (error.errors) {
        // Handle array of errors
        if (Array.isArray(error.errors)) {
          const errorMessages = error.errors.map(err => err.msg || 'Validation error').join(', ');
          setMessage({ text: errorMessages, type: 'error' });
        }
        // Handle single error object
        else if (error.errors.msg) {
          setMessage({ text: error.errors.msg, type: 'error' });
        }
      }
      // Handle error response from the server
      else if (error.response?.data) {
        if (error.response.data.errors) {
          const errorMessages = error.response.data.errors.map(err => err.msg || 'Validation error').join(', ');
          setMessage({ text: errorMessages, type: 'error' });
        } else if (error.response.data.message) {
          setMessage({ text: error.response.data.message, type: 'error' });
        } else {
          setMessage({ text: 'Server error: ' + JSON.stringify(error.response.data), type: 'error' });
        }
      }
      // Handle network errors
      else if (error.message) {
        setMessage({ text: error.message, type: 'error' });
      }
      // Fallback for any other error
      else {
        setMessage({ text: 'Registration failed. Please try again.', type: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="company-form-container">
      <div className="form-header">
        <h1>Company Registration</h1>
        <p>Complete the form below to register your company in our database</p>
      </div>

      {message.text && (
        <div className={`message ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="company-form">
        {/* Section A: Company Information */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-label">A</span>
            <h2>Company Information</h2>
          </div>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="companyName">Company Name *</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="yearOfEstablishment">Year of Establishment</label>
              <input
                type="number"
                id="yearOfEstablishment"
                name="yearOfEstablishment"
                value={formData.yearOfEstablishment}
                onChange={handleChange}
                min="1900"
                max="2024"
              />
            </div>

            <div className="form-group">
              <label htmlFor="organizationType">Type of Organisation</label>
              <select
                id="organizationType"
                name="organizationType"
                value={formData.organizationType}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Service">Service</option>
                <option value="Others">Others</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="hsCode">HS Code</label>
              <input
                type="text"
                id="hsCode"
                name="hsCode"
                value={formData.hsCode}
                onChange={handleChange}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="industrySectors">Industry Sector(s)</label>
              <input
                type="text"
                id="industrySectors"
                name="industrySectors"
                value={formData.industrySectors}
                onChange={handleChange}
                placeholder="e.g., Construction, Engineering, Manufacturing"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="productsServices">Products & Services</label>
              <textarea
                id="productsServices"
                name="productsServices"
                value={formData.productsServices}
                onChange={handleChange}
                rows="3"
                placeholder="Describe your products and services..."
              />
            </div>

            <div className="form-group full-width">
              <label>Product Catalogue</label>
              <div className="catalogue-options">
                <div className="radio-group">
                  <input
                    type="radio"
                    id="upload"
                    name="productCatalogueType"
                    value="upload"
                    checked={formData.productCatalogueType === 'upload'}
                    onChange={handleChange}
                  />
                  <label htmlFor="upload">Upload</label>
                </div>
                <div className="radio-group">
                  <input
                    type="radio"
                    id="url"
                    name="productCatalogueType"
                    value="url"
                    checked={formData.productCatalogueType === 'url'}
                    onChange={handleChange}
                  />
                  <label htmlFor="url">URL</label>
                </div>
              </div>
              {formData.productCatalogueType && (
                <input
                  type={formData.productCatalogueType === 'upload' ? 'file' : 'url'}
                  name="productCatalogue"
                  value={formData.productCatalogueType === 'url' ? formData.productCatalogue : ''}
                  onChange={handleChange}
                  className="catalogue-input"
                />
              )}
            </div>

            <div className="form-group full-width">
              <label>Sales Turnover</label>
              <div className="checkbox-grid">
                {[
                  'Upto 1 crore',
                  '1 crore to 10 crores',
                  '10 crores to 25 crores',
                  '25 crores to 100 crores',
                  '100 crores to 500 crores',
                  '500 crores and above'
                ].map((option, index) => (
                  <div key={index} className="checkbox-option">
                    <input
                      type="radio"
                      id={`turnover-${index}`}
                      name="salesTurnover"
                      value={option}
                      checked={formData.salesTurnover === option}
                      onChange={handleChange}
                    />
                    <label htmlFor={`turnover-${index}`}>{option}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section B: Contact Information */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-label">B</span>
            <h2>Contact Information</h2>
          </div>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="contactName">Name of CEO / MD / Chairman / President / Head *</label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation">Designation</label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone / Mobile *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="registeredOffice">Registered Office Address</label>
              <textarea
                id="registeredOffice"
                name="registeredOffice"
                value={formData.registeredOffice}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="corporateOffice">Corporate Office Address</label>
              <textarea
                id="corporateOffice"
                name="corporateOffice"
                value={formData.corporateOffice}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Section D: Location */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-label">D</span>
            <h2>Location</h2>
          </div>

          <div className="form-group full-width">
            <label>Click on the map to select your location</label>
            
            {formData.latitude && formData.longitude && (
              <p>Selected Location: Latitude {formData.latitude}, Longitude {formData.longitude}</p>
            )}
          </div>
        </div>

        {/* Section C: Account Credentials */}
        <div className="form-section">
          <div className="section-header">
            <span className="section-label">C</span>
            <h2>Account Credentials</h2>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password2">Confirm Password *</label>
              <input
                type="password"
                id="password2"
                name="password2"
                value={formData.password2}
                onChange={handleChange}
                required
                minLength="6"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className={`submit-btn ${isSubmitting ? 'submit-btn-disabled' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;